import React, {
  useState,
  useEffect,
  useRef,
  LegacyRef,
  useCallback,
} from 'react';
import { createUseStyles } from 'react-jss';
import {
  Testimonial,
  updateTestimonialIndexPosition,
} from 'src/slices/testimonial';
import { extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/addon/closest-edge';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/util/combine';
import { useAppDispatch, useAppSelector } from 'src/hooks/store';
import { FormControl, MenuItem, Select, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import { useTranslation } from 'react-i18next';
import TextField from '@mui/material/TextField';
import ButtonBase from '@mui/material/ButtonBase';
import { updatePluginSettings } from '../../slices/auth';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import TestimonialCardWebsite from './TestimonialCardWebsite';
import DeleteIcon from '../../assets/delete-icon.png';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/adapter/element';
import { autoScrollForElements } from '@atlaskit/pragmatic-drag-and-drop-auto-scroll/element';
import Tabs from '../Tabs';
import { reorderWithEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/util/reorder-with-edge';
import { Trigger } from '../../slices/auth';
import { useAppBridge } from '@shopify/app-bridge-react';

const useStyles = createUseStyles({
  TestimonialListItem: {
    display: 'flex',
    padding: '0px',
    width: '100%',
    height: '120px',
    background: 'rgba(237, 240, 255, 0.60)',
    borderRadius: '8px',
  },
  addTestimonialCard: {
    border: '1px dashed #273EAC',
    background: 'white',
    borderRadius: '10px',
    width: '100%',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    paddingTop: '12px',
  },
  addTestimonialText: {
    fontFamily: 'Poppins',
    fontStyle: 'normal',
    fontWeight: '600',
    fontSize: '12px',
    lineHeight: '140%',
    alignSelf: 'flex-start',
    color: '#060640',
    letterSpacing: '0.04em',
    marginBottom: '20px',
    paddingLeft: '16px',
  },
  addTestimonialButton: {
    gap: '11px',
    display: 'flex',
    padding: '4px 24px',
    justifyContent: 'center',
    borderRadius: '8px',
    background: 'white',
    border: '1px solid var(--Bleu-marque, #273EAC)',
    height: '42px',
    alignSelf: 'center',
  },
  verticalDivider: {
    borderLeft: '1px solid #273EAC',
    height: '100%',
    alignSelf: 'center',
  },

  buttonContainerAddTestimonial: {
    display: 'flex',
    alignItems: 'center',
    alignSelf: 'center',
  },
  addTestimonialIcon: {
    width: '40px',
    height: '40px',
    color: '#273EAC',
  },

  urlTextField: {
    '& input': {
      fontFamily: 'Poppins',
      fontStyle: 'normal',
      fontWeight: '400',
      fontSize: '14px',
      lineHeight: '14px',
      color: '#060640',
    },

    '& .MuiOutlinedInput-root': {
      width: '190px',
      height: '32px',
      borderRadius: '8px',
      border: '1px solid #CED3E8',
      background: 'white',
    },
    '& .MuiOutlinedInput-hover': {
      border: '1px solid #CED3E8',
    },
    '& .MuiOutlinedInput-focus': {
      border: '1px solid #CED3E8',
    },
    '& fieldset': {
      border: 'none',
    },
  },

  saveButton: {
    color: 'white',
    height: '40px',
    width: '150px',
    borderRadius: '8px',
    background: '#273EAC',
    alignSelf: 'center',
    fontFamily: 'Poppins',
    fontStyle: 'normal',
    fontWeight: '600',
    fontSize: '14px',
    lineHeight: '14px',
    marginTop: '16px',
    letterSpacing: '-0.4px',
    textAlign: 'center',
    fontFeatureSettings: "'clig' off, 'liga' off",
  },

  filterField: {
    flex: 1,
    '& input': {
      fontFamily: 'Poppins',
      fontStyle: 'normal',
      fontWeight: '400',
      fontSize: '14px',
      lineHeight: '14px',
      color: '#060640',
    },

    '& .MuiOutlinedInput-root': {
      height: '32px',
      borderRadius: '8px',
      border: '1px solid #CED3E8',
      background: 'white',
    },
    '& .MuiOutlinedInput-hover': {
      border: '1px solid #CED3E8',
    },
    '& .MuiOutlinedInput-focus': {
      border: '1px solid #CED3E8',
    },
    '& fieldset': {
      border: 'none',
    },
  },

  filterSelector: {
    flex: 1,
    '& .MuiOutlinedInput-root': {
      height: '32px',
      '& .MuiInputBase-input': {
        fontFamily: 'Poppins',
        fontStyle: 'normal',
        fontWeight: '400',
        fontSize: '14px',
        color: '#060640',
        textAlign: 'center',
      },
    },
  },

  typographySelector: {
    fontFamily: 'Poppins',
    fontStyle: 'normal',
    fontWeight: '400',
    fontSize: '14px',
    color: '#060640',
    textAlign: 'center',
  },

  filterContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  typographyAssociatedUrl: {
    fontFamily: 'Poppins',
    fontStyle: 'normal',
    fontWeight: '600',
    fontSize: '12px',
    lineHeight: '140%',
    color: '#060640',
    textAlign: 'center',
    fontFeatureSettings: "'clig' off, 'liga' off",
  },
  descriptionIcon: {
    display: 'flex',
    alignItems: 'center',
    width: '25px',
    height: '25px',
    borderRadius: '50%',
    background: '#08E8AF',
    justifyContent: 'center',
    color: 'white',
    fontFamily: 'Poppins',
    fontStyle: 'normal',
    fontWeight: '700',
    fontSize: '15px',
    fontFeatureSettings: "'clig' off, 'liga' off",
    alignSelf: 'center',
    position: 'absolute',
    right: '-8px',
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 3,
  },
  descriptionIconUndone: {
    display: 'flex',
    alignItems: 'center',
    width: '25px',
    height: '25px',
    borderRadius: '50%',
    background: '#F2994A',
    justifyContent: 'center',
    color: 'white',
    fontFamily: 'Poppins',
    fontStyle: 'normal',
    fontWeight: '700',
    fontSize: '15px',
    fontFeatureSettings: "'clig' off, 'liga' off",
    alignSelf: 'center',
    position: 'absolute',
    right: '-8px',
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 3,
  },
  buttonAddUrl: {
    width: '50px',
    height: '50px',
  },
  buttonContainer: {
    height: '50px',
    display: 'flex',
    gap: '17px',
    justifyContent: 'center',
  },
  buttonAction: {
    border: '1px solid #273EAC',
    borderRadius: '80px',
    width: '120px',
    gap: '5px',
  },
  testimonialList: {
    listStyleType: 'none',
    scrollBehavior: 'smooth',
    overflowY: 'scroll',
    overflowX: 'hidden',
    width: '100%',
    gap: '16px',
    display: 'flex',
    marginBottom: '16px',
    flexDirection: 'column',
    maxHeight: 'calc(100vh - 395px)',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    '&::-webkit-scrollbar-track': {
      display: 'none',
    },
    '&::-webkit-scrollbar-thumb': {
      display: 'none',
    },
  },

  nextButton: {
    fontFamily: 'Montserrat',
    fontSize: '14px',
    color: 'white',
    backgroundColor: '#273EAC',
    borderRadius: '20px',
    width: '100px',
    height: '30px',
  },
  tooltipContainer: {
    display: 'flex',
    gap: '7px',
    alignItems: 'center',
  },
  previousButton: {
    fontFamily: 'Montserrat',
    fontSize: '10px',
    textAlign: 'center',
    textDecoration: 'underline',
  },

  arrow: {
    '&[data-popper-placement*="right"]': {
      '& .MuiTooltip-arrow': {
        transform: 'translate(-14px, 50px) !important',
      },
    },
    '&[data-popper-placement*="left"]': {
      '& .MuiTooltip-arrow': {
        transform: 'translate(5.1px, 50px) !important',
      },
    },
  },
  tabContainer: {
    width: '100%',
    background: 'rgba(237, 240, 255, 0.60)',
    padding: '12px',
    borderRadius: '8px',
    marginBottom: '16px',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
  },
  typographyTab: {
    fontSize: '14px',
    fontFamily: 'Poppins',
    fontStyle: 'normal',
    fontWeight: '500',
    lineHeight: '100%',
    fontFeatureSettings: "'clig' off, 'liga' off",
  },
  filterContainerAction: {
    width: '100%',
    background: 'rgba(237, 240, 255, 0.60)',
    padding: '12px 16px 16px 12px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '16px',
    position: 'relative',
  },
  urlText: {
    fontFamily: 'Poppins',
    fontStyle: 'normal',
    fontWeight: '600',
    fontSize: '16px',
    lineHeight: '140%',
    color: '#060640',
    textAlign: 'center',
    fontFeatureSettings: "'clig' off, 'liga' off",
    cursor: 'pointer',
  },
});

interface Props {
  testimonials: Testimonial[];
  setting: {
    tag: string;
    urls: string[];
    allPage: boolean;
    option?: { actif: boolean; key: { value: string; type: string }[] };
    trigger?: Trigger;
  };
  settings: {
    tag: string;
    urls: string[];
    allPage: boolean;
    option?: { actif: boolean; key: { value: string; type: string }[] };
  }[];
  setSettings: any;
  limitUrl?: number;
  limitTestimonial?: number;
  tutorial?: boolean;
  setClickedStep3Tutorial?: any;
  isValidUrl?: boolean;
  setIsValidUrl?: React.Dispatch<React.SetStateAction<boolean | undefined>>;
}

const TestimonialListCard: React.FC<Props> = ({
  testimonials,
  setting,
  settings,
  setSettings,
  tutorial,
  setClickedStep3Tutorial,
  isValidUrl,
  setIsValidUrl,
}) => {
  const classes = useStyles();

  const ref = useRef<HTMLDivElement | null>(null);

  const [testimonialsList, setTestimonialsList] =
    useState<Testimonial[]>(testimonials);

  useEffect(() => {
    setTestimonialsList(
      testimonials.sort((a, b) => {
        let diff = 0;
        a.indexPosition.map((tempA) => {
          b.indexPosition.map((tempB) => {
            if (tempA.tag === setting.tag && tempB.tag === setting.tag) {
              diff = tempA.index - tempB.index;
            }
          });
        });
        return diff;
      }),
    );
  }, [testimonials]);

  useEffect(() => {
    const element = ref.current;

    if (!element) {
      throw new Error('ref not set');
    }

    return combine(
      autoScrollForElements({ element }),
      monitorForElements({
        onDrop: ({ source, location }: { source: any; location: any }) => {
          const dropTarget = location.current.dropTargets[0];
          if (!dropTarget) return;

          const startIndex = testimonialsList.findIndex(
            (task) => task._id === source.data.id,
          );

          const indexOfTarget = testimonialsList.findIndex(
            (task) => task._id === dropTarget.data.id,
          );

          if (dropTarget.data.tag != source.data.type) {
            return;
          }

          if (
            dropTarget.data.tag == source.data.type &&
            source.data.type == setting.tag
          ) {
            const reordered = reorderWithEdge({
              list: testimonialsList,
              startIndex,
              indexOfTarget,
              closestEdgeOfTarget: extractClosestEdge(dropTarget.data),
              axis: 'vertical',
            });

            reordered.map((testimonial, index) => {
              dispatch(
                updateTestimonialIndexPosition({
                  testimonialsId: testimonial._id,
                  indexPosition: index,
                  tag: setting.tag,
                }),
              );
            });

            // setTestimonialsList(reordered);
          }
        },
      }),
    );
  }, [testimonialsList]);

  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [showUrl, setShowUrl] = useState(tutorial);

  const [temporaryUrl, setTemporaryUrl] = useState('');

  const [displayedLength, setDisplayedLength] = useState<number>(0);

  const tabs = ['withUrl', 'withFilter'];

  const [value, setValue] = useState(0);

  useEffect(() => {
    if (setting.option?.actif) {
      setValue(1);
    }
  }, [setting]);

  const { user } = useAppSelector(({ auth: { user } }) => ({
    user,
  }));

  const changeTab = (newValue: number) => {
    setValue(newValue);
    if (newValue === 1) {
      setSettings(
        [...settings].map((object) => {
          if (object.tag === setting.tag) {
            if (object?.option?.key.length) {
              return {
                ...object,
                option: {
                  ...object.option,
                  actif: true,
                },
              };
            } else {
              return {
                ...object,
                option: {
                  ...object.option,
                  actif: true,
                  key: [{ value: '', type: '' }],
                },
              };
            }
          } else return object;
        }),
      );
    } else {
      setSettings(
        [...settings].map((object) => {
          if (object.tag === setting.tag) {
            if (object?.urls.length) {
              return {
                ...object,
                option: {
                  ...object.option,
                  actif: false,
                },
              };
            } else {
              return {
                ...object,
                urls: [...object.urls, ''],
                option: {
                  ...object.option,
                  actif: false,
                  key: [{ value: '', type: '' }],
                },
              };
            }
          } else return object;
        }),
      );
    }
  };

  const [displayedUrl, setDisplayedUrl] = useState<string[]>(() => {
    const temp: string[] = [];
    setting.urls.map((setting) => {
      temp.push(setting);
    });
    return temp;
  });

  useEffect(() => {
    settings.map((s) => {
      if (s.tag === setting.tag) {
        setDisplayedUrl(s.urls);
      }
    });
  }, [settings, setting]);

  const saveUrlRef = useRef<HTMLButtonElement>(null);

  const saveUrl = useCallback(() => {
    dispatch(updatePluginSettings({ tagsList: settings })).then((res) => {
      if (res.payload) {
        setShowUrl(false);
      }
    });
  }, [dispatch, settings]);

  useEffect(() => {
    const handleKeyDown = (event: any) => {
      if (event.key === 'Enter') {
        // Perform the desired action when Enter key is pressed
        event.preventDefault();
        if (saveUrlRef.current) {
          saveUrlRef.current.click();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    if (!setting.urls.length) {
      setSettings(
        [...settings].map((object) => {
          if (object.tag === setting.tag) {
            return {
              ...object,
              urls: [...object.urls, ''],
            };
          } else return object;
        }),
      );
    }
    if (!setting.option?.key.length) {
      setSettings(
        [...settings].map((object) => {
          if (object.tag === setting.tag) {
            return {
              ...object,
              option: {
                ...object.option,
                actif: false,
                key: [{ value: '', type: '' }],
              },
            };
          } else return object;
        }),
      );
    }
  }, [setting]);

  const portal = document.createElement('div');
  document.body.appendChild(portal);

  useEffect(() => {
    if (setting.option?.actif) {
      if (setting.option?.key.length) {
        setDisplayedLength(
          setting.option?.key.filter((u) => u.value != '').length,
        );
      }
    } else {
      if (displayedUrl.length) {
        setDisplayedLength(displayedUrl.filter((u) => u != '').length);
      }
    }
  }, [displayedUrl]);

  useEffect(() => {
    if (setClickedStep3Tutorial)
      setClickedStep3Tutorial(displayedLength != 0 && !showUrl);
  }, [displayedLength, showUrl]);

  const productPicker = async () => {
    if (user?.isShopifyAccount) {
      const shopifyApp = useAppBridge();
      try {
        const resourcePicker = await shopifyApp.resourcePicker({
          type: 'product',
          action: 'select',
          multiple: true,
          filter: {
            variants: false,
          },
        });
        if (resourcePicker) {
          const productList: string[] = [];
          resourcePicker.forEach((product) => {
            const url =
              'https://' +
              shopifyApp.config.shop +
              '/products/' +
              product.handle;

            productList.push(url);
          });
          setSettings(
            [...settings].map((object) => {
              if (object.tag === setting.tag) {
                setTemporaryUrl('');
                return {
                  ...object,
                  urls: [
                    ...object.urls.filter((url) => {
                      return url != '' && !productList.includes(url);
                    }),
                    ...productList,
                  ],
                };
              } else {
                return object;
              }
            }),
          );
        }
      } catch (error) {
        // Handle error
        console.log('Error while fetching authorization code', error);
      }
    }
  };

  return (
    <div>
      {showUrl ? (
        <div className={classes.tabContainer}>
          <Tabs
            value={value}
            changeTab={(_, newValue) => changeTab(newValue)}
            tabsValue={tabs.map(
              (tab) => `${t(`pages.Testimonials.filter.${tab}`)}`,
            )}
            secondClassName={classes.typographyTab}
          />
          {value == 1 && setting?.option ? (
            <>
              {setting.option.key && (
                <>
                  {setting.option.key.map((item, indexKey: number) => (
                    <div className={classes.filterContainer} key={indexKey}>
                      <FormControl className={classes.filterSelector}>
                        <Select
                          value={item.type ? item.type : 'startswith'}
                          placeholder="Filter"
                          defaultValue="startswith"
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                backgroundColor: 'white',
                                filter:
                                  'drop-shadow(5px 4px 4px rgba(0, 0, 0, 0.25))',
                              },
                            },
                          }}
                          onChange={(e) => {
                            setSettings(
                              [...settings].map((object) => {
                                if (
                                  object.tag === setting.tag &&
                                  object.option
                                ) {
                                  return {
                                    ...object,
                                    option: {
                                      ...object.option,
                                      key: [
                                        ...object?.option.key.map(
                                          (
                                            o: {
                                              value: string;
                                              type: string;
                                            },
                                            index,
                                          ) => {
                                            if (index === indexKey) {
                                              return {
                                                type: e.target.value,
                                                value: o.value,
                                              };
                                            } else return o;
                                          },
                                        ),
                                      ],
                                    },
                                  };
                                } else return object;
                              }),
                            );
                          }}
                        >
                          <MenuItem
                            className={classes.typographySelector}
                            value={'startswith'}
                          >
                            {t('pages.Testimonials.filter.startswith')}
                          </MenuItem>
                          <MenuItem
                            className={classes.typographySelector}
                            value={'endswith'}
                          >
                            {t('pages.Testimonials.filter.endswith')}
                          </MenuItem>
                          <MenuItem
                            className={classes.typographySelector}
                            value={'regex'}
                          >
                            {t('pages.Testimonials.filter.regex')}
                          </MenuItem>
                          <MenuItem
                            className={classes.typographySelector}
                            value={'contains'}
                          >
                            {t('pages.Testimonials.filter.contains')}
                          </MenuItem>
                          <MenuItem
                            className={classes.typographySelector}
                            value={'dontcontains'}
                          >
                            {t('pages.Testimonials.filter.dontcontains')}
                          </MenuItem>
                          <MenuItem
                            className={classes.typographySelector}
                            value={'exactlymatch'}
                          >
                            {t('pages.Testimonials.filter.exactlymatch')}
                          </MenuItem>
                          <MenuItem
                            className={classes.typographySelector}
                            value={'dontexactlymatch'}
                          >
                            {t('pages.Testimonials.filter.dontexactlymatch')}
                          </MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        value={item.value}
                        className={classes.filterField}
                        onChange={(e) => {
                          setSettings(
                            [...settings].map((object) => {
                              if (object.tag === setting.tag && object.option) {
                                return {
                                  ...object,
                                  option: {
                                    ...object.option,
                                    key: [
                                      ...object?.option.key.map(
                                        (
                                          o: {
                                            value: string;
                                            type: string;
                                          },
                                          index,
                                        ) => {
                                          if (index === indexKey) {
                                            return {
                                              type: o.type,
                                              value: e.target.value,
                                            };
                                          } else return o;
                                        },
                                      ),
                                    ],
                                  },
                                };
                              } else return object;
                            }),
                          );
                        }}
                      />
                      <Button
                        style={{
                          borderRadius: '40%',
                          width: '40px',
                          height: '40px',
                          minWidth: '40px',
                        }}
                        onClick={() => {
                          setSettings(
                            [...settings].map((object) => {
                              if (object.tag === setting.tag) {
                                return {
                                  ...object,
                                  option: {
                                    ...object.option,
                                    key: object?.option?.key.filter(
                                      (
                                        o: {
                                          value: string;
                                          type: string;
                                        },
                                        index,
                                      ) => {
                                        if (index === indexKey) {
                                          return false;
                                        } else return true;
                                      },
                                    ),
                                  },
                                };
                              } else return object;
                            }),
                          );
                        }}
                      >
                        <img src={DeleteIcon} />
                      </Button>

                      {setting.option &&
                        indexKey == setting.option.key.length - 1 && (
                          <Button
                            style={{
                              borderRadius: '40%',
                              width: '40px',
                              height: '40px',
                              minWidth: '40px',
                            }}
                            onClick={() => {
                              setSettings(
                                [...settings].map((object) => {
                                  if (
                                    object.tag === setting.tag &&
                                    object.option
                                  ) {
                                    return {
                                      ...object,
                                      option: {
                                        ...object.option,
                                        key: [
                                          ...object?.option.key,
                                          { value: '', type: '' },
                                        ],
                                      },
                                    };
                                  } else return object;
                                }),
                              );
                            }}
                            className={classes.buttonAddUrl}
                          >
                            <AddCircleOutlineIcon
                              style={{
                                color: '#273EAC',
                              }}
                            />
                          </Button>
                        )}
                    </div>
                  ))}
                </>
              )}
            </>
          ) : (
            <>
              <div>
                {displayedUrl.map((urlItem, index) => (
                  <React.Fragment key={index}>
                    <div
                      key={index}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                      }}
                    >
                      <TextField
                        id="outlined-basic"
                        placeholder={t('pages.Settings.addUrl')}
                        variant="outlined"
                        value={urlItem}
                        autoComplete="off"
                        className={classes.urlTextField}
                        onChange={(e) => {
                          setSettings(
                            [...settings].map((object) => {
                              if (object.tag === setting.tag) {
                                let urls: string[] = [];
                                urls = [...object.urls];
                                urls[index] = e.target.value;
                                return {
                                  ...object,
                                  urls: urls,
                                };
                              } else return object;
                            }),
                          );
                        }}
                      />

                      <div>
                        <Button
                          style={{
                            borderRadius: '40%',
                            width: '40px',
                            height: '40px',
                            minWidth: '40px',
                          }}
                          onClick={() => {
                            setSettings(
                              [...settings].map((object) => {
                                if (object.tag === setting.tag) {
                                  return {
                                    ...object,
                                    urls: object.urls.filter(
                                      (item) => item !== urlItem,
                                    ),
                                  };
                                } else return object;
                              }),
                            );
                          }}
                        >
                          <img src={DeleteIcon} />
                        </Button>
                        {index == displayedUrl.length - 1 && (
                          <>
                            <Button
                              style={{
                                borderRadius: '40%',
                                width: '40px',
                                height: '40px',
                                minWidth: '40px',
                              }}
                              onClick={() => {
                                setSettings(
                                  [...settings].map((object) => {
                                    if (object.tag === setting.tag) {
                                      setTemporaryUrl('');

                                      return {
                                        ...object,
                                        urls: [...object.urls, temporaryUrl],
                                      };
                                    } else {
                                      return object;
                                    }
                                  }),
                                );
                              }}
                              className={classes.buttonAddUrl}
                            >
                              <AddCircleOutlineIcon
                                style={{
                                  color: '#273EAC',
                                }}
                              />
                            </Button>
                            {user?.isShopifyAccount && (
                              <Button
                                style={{
                                  borderRadius: '40%',
                                  width: '40px',
                                  height: '40px',
                                  minWidth: '40px',
                                }}
                                onClick={() => {
                                  productPicker();
                                }}
                                className={classes.buttonAddUrl}
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  x="0px"
                                  y="0px"
                                  width="40"
                                  height="40"
                                  viewBox="0 0 30 30"
                                >
                                  {' '}
                                  <path d="M 13 1 C 11.393993 1 10.138283 2.1848392 9.3027344 3.6132812 C 8.6657258 4.7023016 8.2470434 5.9595896 8.0820312 7.1191406 L 5.6679688 7.6679688 L 3 24 L 18.332031 27 L 27 25.078125 L 24.332031 7 L 22.332031 6.5 L 20.332031 4.3320312 L 18.240234 4.8085938 C 18.128819 4.4221214 17.98999 4.0387914 17.802734 3.6816406 C 17.334682 2.7889297 16.562308 2 15.5 2 C 15.340386 2 15.191357 2.029562 15.041016 2.0546875 C 14.922231 1.8901067 14.792252 1.7332776 14.638672 1.5976562 C 14.217828 1.2260244 13.650428 1 13 1 z M 13 2 C 13.435572 2 13.725032 2.123585 13.976562 2.3457031 C 13.99876 2.3653051 14.015594 2.3984555 14.037109 2.4199219 C 13.720913 2.6050566 13.429473 2.8308585 13.181641 3.0976562 C 12.595251 3.7289177 12.172865 4.5276755 11.855469 5.3125 C 11.713212 5.6642582 11.598477 6.0094274 11.496094 6.3417969 L 9.1523438 6.875 C 9.3336887 5.9539384 9.673596 4.9609735 10.166016 4.1191406 C 10.903217 2.8588328 11.897007 2 13 2 z M 15.535156 3.0078125 C 16.081074 3.0260252 16.548246 3.4450442 16.916016 4.1464844 C 17.057627 4.4165787 17.167947 4.7216432 17.259766 5.03125 L 15.958984 5.3261719 C 15.908173 4.5335392 15.794795 3.7254813 15.535156 3.0078125 z M 14.554688 3.2617188 C 14.566594 3.2921492 14.582366 3.3143054 14.59375 3.3457031 C 14.818332 3.9651307 14.925561 4.7577661 14.970703 5.5507812 L 12.646484 6.0800781 C 12.69407 5.9477768 12.729373 5.8206067 12.783203 5.6875 C 13.072432 4.9723245 13.455421 4.2710822 13.914062 3.7773438 C 14.115999 3.5599546 14.329456 3.3913651 14.554688 3.2617188 z M 14.128906 9.6640625 C 14.443906 9.6640625 14.735813 9.6808906 15.007812 9.7128906 C 15.282813 9.7448906 15.533859 9.7935156 15.755859 9.8535156 C 15.980859 9.9125156 16.174031 9.9816406 16.332031 10.056641 L 15.371094 12.792969 C 15.244094 12.733969 15.090875 12.671234 14.921875 12.615234 C 14.750875 12.556234 14.565375 12.500078 14.359375 12.455078 C 14.158375 12.410078 13.938937 12.371609 13.710938 12.349609 C 13.482938 12.327609 13.242141 12.320984 12.994141 12.333984 C 12.766141 12.346984 12.562859 12.385312 12.380859 12.445312 C 12.201859 12.504312 12.043016 12.588453 11.916016 12.689453 C 11.789016 12.792453 11.643219 12.912734 11.574219 13.052734 C 11.502219 13.192734 11.386719 13.323813 11.386719 13.757812 C 11.386719 14.265813 11.722969 14.625531 12.167969 14.894531 C 13.167969 15.499531 14.996094 16.499156 14.996094 19.035156 C 14.996094 19.826156 14.772844 20.504359 14.464844 21.068359 C 14.161844 21.624359 13.744234 22.060906 13.240234 22.378906 C 12.747234 22.687906 12.176641 22.881984 11.556641 22.958984 C 10.953641 23.033984 10.502938 23.001234 9.8359375 22.865234 C 9.8309375 22.865234 9.8233594 22.863281 9.8183594 22.863281 C 9.8133594 22.863281 9.8076875 22.859375 9.8046875 22.859375 C 9.7996875 22.859375 9.7921094 22.857422 9.7871094 22.857422 C 9.7821094 22.857422 9.7745313 22.853516 9.7695312 22.853516 C 9.4575313 22.784516 9.1550469 22.692937 8.8730469 22.585938 C 8.5930469 22.480937 8.32975 22.362469 8.09375 22.230469 C 7.85775 22.101469 7.6449844 21.966172 7.4589844 21.826172 C 7.2769844 21.688172 7.121 21.542531 7 21.394531 L 7.7363281 19.123047 C 7.8603281 19.220047 8.0135469 19.331312 8.1855469 19.445312 C 8.3615469 19.561312 8.557625 19.677109 8.765625 19.787109 C 8.978625 19.898109 9.2045 19.998031 9.4375 20.082031 C 9.6735 20.166031 9.9182031 20.233625 10.158203 20.265625 C 10.371203 20.294625 10.561516 20.290859 10.728516 20.255859 C 10.895516 20.220859 11.041203 20.155453 11.158203 20.064453 C 11.277203 19.976453 11.369594 19.862422 11.433594 19.732422 C 11.497594 19.600422 11.533062 19.456781 11.539062 19.300781 C 11.547062 19.144781 11.530234 18.996469 11.490234 18.855469 C 11.447234 18.712469 11.378297 18.5755 11.279297 18.4375 C 11.180297 18.2995 11.051625 18.160578 10.890625 18.017578 C 10.729625 17.874578 10.536594 17.729219 10.308594 17.574219 C 10.025594 17.378219 9.765125 17.186922 9.453125 16.919922 C 9.204125 16.707922 8.9980781 16.435969 8.8300781 16.167969 C 8.6630781 15.902969 8.5303594 15.616687 8.4433594 15.304688 C 8.3563594 14.995688 8.3189375 14.661875 8.3359375 14.296875 C 8.3649375 13.687875 8.4948906 13.125188 8.7128906 12.617188 C 8.9358906 12.106188 9.2495781 11.648906 9.6425781 11.253906 C 10.043578 10.850906 10.53075 10.508047 11.09375 10.248047 C 11.67375 9.9800469 12.420344 9.8184688 13.152344 9.7304688 C 13.492344 9.6834687 13.820906 9.6640625 14.128906 9.6640625 z"></path>{' '}
                                </svg>
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </>
          )}
          {tutorial && <div className={classes.descriptionIconUndone}>2</div>}
          <ButtonBase
            className={classes.saveButton}
            onClick={() => {
              saveUrl();
              if (setIsValidUrl) {
                if (displayedUrl.length === 1 && displayedUrl[0] === '') {
                  setIsValidUrl(false);
                } else {
                  setIsValidUrl(true);
                }
              }
            }}
            ref={saveUrlRef}
          >
            {t('common.save')}
          </ButtonBase>
        </div>
      ) : (
        <div className={classes.filterContainerAction}>
          {displayedLength != 0 ? (
            <>
              <Typography className={`${classes.typographyAssociatedUrl}`}>
                {setting.option?.actif
                  ? t('pages.Testimonials.filter.applyedFilter')
                  : t('pages.Testimonials.associatedUrls')}{' '}
                {'('}
                {displayedLength}
                {')'}
              </Typography>

              <ButtonBase
                onClick={() => {
                  setShowUrl(true);
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M18.5126 4.4915L19.4926 5.47251C19.8198 5.80002 20 6.23577 20 6.69921C20 7.16265 19.8198 7.5984 19.4926 7.92578L18.2922 9.12746L14.8616 5.69318L16.062 4.4915C16.7162 3.83648 17.8577 3.83585 18.5126 4.4915ZM5.07744 15.4874L13.8809 6.67398L17.3116 10.1084L8.50744 18.9212C8.46315 18.9655 8.40769 18.9974 8.34665 19.0126L4.43009 19.9896C4.40243 19.9966 4.374 20 4.34621 20C4.25547 20 4.16676 19.9639 4.10166 19.8987C4.01562 19.8126 3.98098 19.6871 4.01016 19.5691L4.98594 15.6484C5.00117 15.588 5.03315 15.5319 5.07744 15.4874Z"
                    fill="#060640"
                  />
                </svg>
              </ButtonBase>
              {tutorial && <div className={classes.descriptionIcon}>2</div>}
            </>
          ) : (
            <>
              <Typography
                className={`${classes.typographyAssociatedUrl}`}
                style={{ color: 'red' }}
              >
                {t('pages.Testimonials.associateUrls')}
              </Typography>
              <ButtonBase
                onClick={() => {
                  setShowUrl(true);
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M18.5126 4.4915L19.4926 5.47251C19.8198 5.80002 20 6.23577 20 6.69921C20 7.16265 19.8198 7.5984 19.4926 7.92578L18.2922 9.12746L14.8616 5.69318L16.062 4.4915C16.7162 3.83648 17.8577 3.83585 18.5126 4.4915ZM5.07744 15.4874L13.8809 6.67398L17.3116 10.1084L8.50744 18.9212C8.46315 18.9655 8.40769 18.9974 8.34665 19.0126L4.43009 19.9896C4.40243 19.9966 4.374 20 4.34621 20C4.25547 20 4.16676 19.9639 4.10166 19.8987C4.01562 19.8126 3.98098 19.6871 4.01016 19.5691L4.98594 15.6484C5.00117 15.588 5.03315 15.5319 5.07744 15.4874Z"
                    fill="#060640"
                  />
                </svg>
              </ButtonBase>
              {tutorial && (
                <div
                  className={classes.descriptionIconUndone}
                  style={!isValidUrl ? { backgroundColor: 'red' } : {}}
                >
                  2
                </div>
              )}
            </>
          )}
        </div>
      )}

      <li
        className={classes.testimonialList}
        style={
          user?.step === 2
            ? { maxHeight: 'calc(100vh - 378px)' }
            : { maxHeight: 'calc(100vh - 395px)' }
        }
        ref={ref as LegacyRef<HTMLLIElement>}
      >
        {testimonialsList.map((testimonial, index) => (
          <React.Fragment key={testimonial._id}>
            <div key={testimonial._id} className={classes.TestimonialListItem}>
              <TestimonialCardWebsite
                key={testimonial._id}
                testimonial={testimonial}
                deleteTag={setting.tag}
                index={index}
                tag={setting.tag}
              />
            </div>
          </React.Fragment>
        ))}
      </li>
    </div>
  );
};
export default TestimonialListCard;
